import React from 'react'
import PropTypes from 'prop-types'
import './TeamPerformance.scss'

/**
 * TeamPerformance - Team member performance display
 *
 * @param {Array} members - Array of team member objects
 * @param {string} title - Widget title
 * @param {number} maxItems - Maximum items to display
 * @param {boolean} showViewAll - Show "View All" link
 * @param {Function} onViewAll - View all callback
 * @param {Function} onMemberClick - Member click callback
 * @param {boolean} loading - Show loading state
 */
const TeamPerformance = ({
	members = [],
	title = 'Team Performance',
	maxItems = 5,
	showViewAll = false,
	onViewAll,
	onMemberClick,
	loading = false
}) => {
	const getPerformanceLevel = (completionRate) => {
		if (completionRate >= 90) return 'excellent'
		if (completionRate >= 70) return 'good'
		if (completionRate >= 50) return 'average'
		return 'needs-improvement'
	}

	const displayedMembers = members.slice(0, maxItems)

	if (loading) {
		return (
			<div className="team-performance">
				<div className="team-performance__header">
					<h3 className="team-performance__title">{title}</h3>
				</div>
				<div className="team-performance__list">
					{[...Array(3)].map((_, i) => (
						<div key={i} className="team-performance__item team-performance__item--skeleton">
							<div className="skeleton skeleton--avatar"></div>
							<div className="skeleton skeleton--content">
								<div className="skeleton skeleton--name"></div>
								<div className="skeleton skeleton--role"></div>
							</div>
							<div className="skeleton skeleton--stats"></div>
						</div>
					))}
				</div>
			</div>
		)
	}

	return (
		<div className="team-performance">
			<div className="team-performance__header">
				<h3 className="team-performance__title">{title}</h3>
				{showViewAll && onViewAll && (
					<button className="team-performance__view-all" onClick={onViewAll}>
						View All
					</button>
				)}
			</div>

			{displayedMembers.length === 0 ? (
				<div className="team-performance__empty">
					<svg viewBox="0 0 24 24" fill="currentColor" className="team-performance__empty-icon">
						<path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
					</svg>
					<p>No team members</p>
				</div>
			) : (
				<div className="team-performance__list">
					{displayedMembers.map((member, index) => {
						const completionRate = member.completion_rate || 0
						const level = getPerformanceLevel(completionRate)

						return (
							<div
								key={member.id || index}
								className={`team-performance__item team-performance__item--${level}`}
								onClick={() => onMemberClick && onMemberClick(member)}
								role={onMemberClick ? 'button' : undefined}
								tabIndex={onMemberClick ? 0 : undefined}
							>
								<div className="team-performance__avatar">
									{member.avatar ? (
										<img src={member.avatar} alt={member.name} />
									) : (
										<span>{member.name?.charAt(0) || '?'}</span>
									)}
								</div>

								<div className="team-performance__info">
									<p className="team-performance__name">{member.name}</p>
									<span className="team-performance__role">{member.role || 'Team Member'}</span>
								</div>

								<div className="team-performance__stats">
									<div className="team-performance__rate">
										<span className="team-performance__rate-value">{completionRate}%</span>
										<span className="team-performance__rate-label">Completion</span>
									</div>

									<div className="team-performance__progress">
										<div
											className="team-performance__progress-fill"
											style={{ width: `${completionRate}%` }}
										/>
									</div>

									{member.tasks_completed !== undefined && (
										<span className="team-performance__task-count">
											{member.tasks_completed} / {member.tasks_total || 0} tasks
										</span>
									)}
								</div>
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}

TeamPerformance.propTypes = {
	members: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			name: PropTypes.string.isRequired,
			role: PropTypes.string,
			avatar: PropTypes.string,
			completion_rate: PropTypes.number,
			tasks_completed: PropTypes.number,
			tasks_total: PropTypes.number
		})
	),
	title: PropTypes.string,
	maxItems: PropTypes.number,
	showViewAll: PropTypes.bool,
	onViewAll: PropTypes.func,
	onMemberClick: PropTypes.func,
	loading: PropTypes.bool
}

export default TeamPerformance
